import { AuthGuard } from '#components/tools/auth-guard.tsx'
import { NotFoundPage } from '#components/tools/not-found-page.tsx'
import { CompanyLessPath } from '#components/domain/auth/user/shared/company-less-path.tsx'
import { CompanyUnlockScreen } from '#components/entities/users/company-lock/company-unlock-screen.tsx'
import { useGlobalState, useSessionState } from '#core/state/State.ts'
import { createFileRoute, Outlet, useMatches, useNavigate, useRouterState } from '@tanstack/react-router'
import { UserGeneralSettings } from '#core/state/UserGeneralSettings.ts'
import { SidebarState, useSidebarCollapse } from '#core/state/ui/SidebarState.ts'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppMode } from '#hooks/use-app-mode.ts'
import { AssumedCompanyFrame } from '#components/primitives/components/assumed-company-frame.tsx'
import { useCanonicalCompanyUrl, useUserState } from '#core/state/UserState.ts'
import { wasAccountExpired } from '#core/auth/account-expired.ts'
import { PermissionAction, PermissionService } from '#core/entities/auth/roles/Permission.ts'
import { BugReportModal } from '#components/domain/operations/bug-reports/bug-report-modal.tsx'
import { readGatePairs } from '#core/entities/auth/roles/permission-gate-helpers.ts'
import { isServiceMenuVisible } from '#components/domain/operations/settings/general/menu-visibility.ts'
import { useMenuVisibilityState } from '#components/domain/operations/settings/general/menu-visibility-state.ts'
import { useHelpSearchState } from '#components/domain/auth/help/help-search-state.ts'
import { HelpCommandPalette } from '#components/domain/auth/help/help-command-palette/index.tsx'
import { useShortcut } from '#hooks/use-shortcut.ts'
import { AppShell } from '#components/generic/app-shell/index.ts'
import { Sidebar, NotificationsBlockedWarning } from '#components/generic/sidebar/index.ts'
import type { AppNavLink } from '#components/generic/sidebar/index.ts'
import { LegacyAppShell } from '#components/composites/app-shell/legacy-app-shell.tsx'
import { APP_NAV_LINKS } from '#components/tools/app-nav-links.ts'

export const Route = createFileRoute('/_app')({
  component: RouteComponent,
})

// Bottom-nav split: primary tabs are an explicit ordered set (Work Requests, Work Orders,
// Assets) mapped onto permission-filtered links — a missing module shrinks the bar rather
// than backfilling. Every other module falls into the More sheet.
const APP_NAV_PRIMARY_HREFS = [
  '/$companyId/work-requests',
  '/$companyId/work-orders',
  '/$companyId/assets',
] as const

function RouteComponent() {
  const sidebarState = useSessionState(SidebarState, 'sbs')
  const generalSettings = useGlobalState(UserGeneralSettings, ['language'])
  const appMode = useAppMode()
  const navigate = useNavigate()
  const userState = useUserState()
  const companyId = userState.activeCompany?.code
  // Upgrade a GUID company segment in the URL to the readable company code once it's known.
  useCanonicalCompanyUrl()
  const { t } = useTranslation('navigation')
  const { t: tAuth } = useTranslation('auth')
  // Shared help-search singleton (same instance the /help route binds). Scope the
  // shell's subscription to $isOpen so typing in the palette never re-renders the whole shell.
  const helpState = useHelpSearchState(['$isOpen'])
  const toggleHelp = useCallback(
    () => (helpState.$isOpen ? helpState.close() : helpState.open()),
    [helpState],
  )
  // Global Ctrl+H opens/closes the palette. Mounted once here so it covers every in-app page.
  useShortcut('ctrl+h', toggleHelp)
  // Remember the current in-app location so "Back to app" from settings returns here.
  // Must read from the *committed* router state (`matches` + `resolvedLocation`), not
  // `useLocation()`: during navigation into settings, `useLocation()` briefly returns
  // the settings path while `matches` still holds `_app` — recording it would make
  // "Back to app" a no-op that returns to settings.
  const appHref = useRouterState({
    select: (s) =>
      s.matches.some((m) => m.routeId === '/_app')
        ? (s.resolvedLocation?.href ?? s.location.href)
        : null,
  })
  useEffect(() => {
    if (appHref) sidebarState.setLastAppPath(appHref)
  }, [appHref, sidebarState])
  // Only show module links the user may view (full READ or any READ_LIMITED
  // slot). While login is still resolving, keep gated links hidden so
  // unauthorized modules never flash in.
  // Org admins can hide some modules from the menu (Settings → General), stored org-wide on
  // company settings. Permissions are unaffected; a page wrapped in `FeatureGuard` is also withheld.
  const menuVisibility = useMenuVisibilityState().menuVisibility
  const visibleLinks = APP_NAV_LINKS.filter((link) => {
    if (link.service && (userState.loginState !== 'success' || !userState.hasAnyPermission(readGatePairs(link.service)))) {
      return false
    }
    return isServiceMenuVisible(link.service, menuVisibility)
  })
  const moduleLinks = visibleLinks.filter((link) => link.service != null)
  const settingsLink = visibleLinks.find((link) => link.service == null)
  const primaryLinks = APP_NAV_PRIMARY_HREFS
    .map((href) => moduleLinks.find((link) => link.href === href))
    .filter((link): link is (typeof moduleLinks)[number] => link != null)
  const primarySet = new Set(primaryLinks)
  const moreLinks = moduleLinks.filter((link) => !primarySet.has(link))

  // Quick "report a bug / request a feature" entry surfaced right in the rail, so users
  // don't have to dig into Settings › Bug reports. Gated on CREATE like the settings button.
  const [bugReportOpen, setBugReportOpen] = useState(false)
  const canReportBug = userState.loginState === 'success'
    && userState.hasPermission(PermissionService.BUG_REPORTS, PermissionAction.CREATE)

  const sidebarCollapse = useSidebarCollapse()
  // Routes on the design-system shell set `staticData.appShell`; every other route keeps the
  // Bootstrap shell until it moves over.
  const usesAppShell = useMatches({
    select: (matches) => matches.some((match) => match.staticData.appShell === true),
  })
  const shellLinks: AppNavLink[] = visibleLinks
    .filter((link) => link.position.includes('top'))
    .flatMap((link) => {
      const children = link.children
        ?.filter((child) => !child.superUserOnly || userState.isSuperAdmin)
        .filter((child) => !child.service || userState.hasPermission(child.service, PermissionAction.READ))
        .map((child) => ({ label: t('side_menu.' + child.titleKey), to: child.href }))
      // An item whose every sub-page is out of reach is left out rather than shown empty.
      if (children && children.length === 0) return []
      return [{ label: t('side_menu.' + link.titleKey), icon: link.glyph ?? link.icon, to: children?.[0]?.to ?? link.href, children, group: link.group }]
    })
  const userName = [userState.user?.first_name, userState.user?.last_name].filter(Boolean).join(' ') || undefined

  const handleLanguageChange = (lang: string) => {
    generalSettings.language = lang
    void generalSettings.save()
  }
  const logout = () => { void navigate({ to: '/logout' }) }
  const openHelp = () => helpState.open()

  if (sidebarState.unauthorized) {
    return <div className='p-5 m-5'>{wasAccountExpired() ? tAuth('accountExpired') : tAuth('sessionExpired')}</div>
  }
  return (
    <AuthGuard
      navigateToLoginOnFailure={true}
      noAccess={<CompanyLessPath fallback={<NotFoundPage />} />}
      locked={(companyId) => <CompanyUnlockScreen companyId={companyId} />}
    >
      {/* One palette instance for the whole authed app; opened from Ctrl+H, the desktop ? button, or the mobile More sheet. */}
      <HelpCommandPalette />
      {canReportBug && (
        <BugReportModal isOpen={bugReportOpen} onClose={() => setBugReportOpen(false)} />
      )}
      {/* Danger ring + "logged in as" label while a super user works inside another company. */}
      <AssumedCompanyFrame />
      {usesAppShell ? (
        <AppShell
          collapsed={sidebarCollapse.collapsed}
          onToggleCollapse={() => sidebarCollapse.toggle()}
          sidebar={
            <Sidebar
              links={shellLinks}
              collapsed={sidebarCollapse.collapsed}
              settingsTo={companyId ? '/$companyId/settings' : undefined}
              onReportBug={canReportBug ? () => setBugReportOpen(true) : undefined}
              headerAccessory={<NotificationsBlockedWarning />}
              onHelp={openHelp}
              onLogout={logout}
              onLanguageChange={handleLanguageChange}
              userName={userName}
              personalSettingsTo="/settings/user"
            />
          }
        >
          <Outlet />
        </AppShell>
      ) : (
        <LegacyAppShell
          topLinks={visibleLinks.filter((link) => link.position.includes('top'))}
          bottomLinks={visibleLinks.filter((link) => link.position.includes('bottom'))}
          collapsed={sidebarCollapse.collapsed}
          onToggleCollapse={() => sidebarCollapse.toggle()}
          appMode={appMode}
          companyId={companyId}
          onReportBug={canReportBug ? () => setBugReportOpen(true) : undefined}
          headerAccessory={<NotificationsBlockedWarning />}
          onLanguageChange={handleLanguageChange}
          onLogout={logout}
          onHelp={openHelp}
          primaryLinks={primaryLinks}
          moreLinks={moreLinks}
          settingsLink={settingsLink}
        >
          <Outlet />
        </LegacyAppShell>
      )}
    </AuthGuard>
  )
}
